import React, { useState } from 'react';
import { type Task } from '../../../services/projectManagement/tasksService';

interface CalendarViewProps {
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  onDateChange?: (date: Date, taskId: string) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ tasks, onTaskClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calendar calculation helpers
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Group tasks by date
  const tasksByDate: Record<string, Task[]> = {};
  tasks.forEach(task => {
    if (task.due_date) {
      const dateKey = task.due_date.split('T')[0];
      if (!tasksByDate[dateKey]) {
        tasksByDate[dateKey] = [];
      }
      tasksByDate[dateKey].push(task);
    }
  });

  // Generate calendar days
  const calendarDays: Array<{ day: number | null; date: Date | null; isCurrentMonth: boolean }> = [];
  
  // Previous month days
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push({ day: null, date: null, isCurrentMonth: false });
  }
  
  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    calendarDays.push({ day, date, isCurrentMonth: true });
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
        <button
          onClick={previousMonth}
          className="rounded-lg px-3 py-2 hover:bg-gray-100"
        >
          ← Previous
        </button>
        
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-900">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <button
            onClick={goToToday}
            className="rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100"
          >
            Today
          </button>
        </div>

        <button
          onClick={nextMonth}
          className="rounded-lg px-3 py-2 hover:bg-gray-100"
        >
          Next →
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div
              key={day}
              className="border-r border-gray-200 px-2 py-3 text-center text-sm font-semibold text-gray-700 last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((dayInfo, index) => {
            const dateKey = dayInfo.date ? formatDateKey(dayInfo.date) : '';
            const dayTasks = tasksByDate[dateKey] || [];
            const hasOverdue = dayTasks.some(
              task => new Date(task.due_date!) < new Date() && task.status !== 'done'
            );

            return (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b border-gray-200 p-2 ${
                  !dayInfo.isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                } ${isToday(dayInfo.date) ? 'bg-blue-50 ring-2 ring-inset ring-blue-500' : ''} last:border-r-0`}
              >
                {dayInfo.day && (
                  <>
                    {/* Day Number */}
                    <div className={`mb-1 text-sm font-semibold ${
                      isToday(dayInfo.date) ? 'text-blue-600' : 'text-gray-700'
                    }`}>
                      {dayInfo.day}
                      {hasOverdue && (
                        <span className="ml-1 text-red-600">⚠️</span>
                      )}
                    </div>

                    {/* Tasks */}
                    <div className="space-y-1">
                      {dayTasks.slice(0, 3).map(task => (
                        <button
                          key={task.id}
                          onClick={() => onTaskClick(task.id)}
                          className="w-full rounded px-2 py-1 text-left text-xs hover:opacity-80 transition-opacity"
                          style={{
                            backgroundColor: task.priority === 'urgent' ? '#fee2e2' :
                                           task.priority === 'high' ? '#fed7aa' :
                                           task.priority === 'medium' ? '#fef3c7' :
                                           '#d1fae5',
                          }}
                          title={task.title}
                        >
                          <div className="flex items-center gap-1">
                            <div className={`h-1.5 w-1.5 rounded-full ${getPriorityColor(task.priority)}`} />
                            <span className="truncate text-gray-900">{task.title}</span>
                          </div>
                        </button>
                      ))}
                      {dayTasks.length > 3 && (
                        <div className="px-2 text-xs text-gray-500">
                          +{dayTasks.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 rounded-lg border border-gray-200 bg-white p-3 text-sm">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-red-500" />
          <span className="text-gray-700">Urgent</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-orange-500" />
          <span className="text-gray-700">High</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-yellow-500" />
          <span className="text-gray-700">Medium</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-green-500" />
          <span className="text-gray-700">Low</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
